import { StandardEvents } from '@shopify/events';

/**
 * Bigens bundle builder v3 (T09 → T41b), `<product-deal-builder>` in blocks/product-deal-builder.liquid.
 *
 * Ball 1 is whatever the stock variant picker has selected. The builder owns the set (the extra balls), renders it
 * through <bigens-set-tray> (slots, tier bar, nudge, add button), runs the sheet (change a ball / add a ball: family
 * tabs → size → colour), mirrors the set on the add-to-cart buttons ("Add 2 balls to cart · €157,70"), writes the
 * "You save · arrives about · ships" line under them, feeds the desktop <bigens-sticky-bar>, and hands the cart lines
 * to product-form.js through getCartItems().
 *
 * All copy comes from the block's `data-bundle-strings` JSON (locales `bigens.deal_builder.*`); prices are cents in the
 * presentment currency, formatted with the shop money format. Set state goes to localStorage for T42 (tray store).
 *
 * @typedef {{ v: string, label: string, note?: string, hex?: string }} OptionValue
 * @typedef {{ name: string, kind: 'size' | 'color' | 'other', values: OptionValue[] }} ProductOption
 * @typedef {{ id: number, o: string[], p: number, a: boolean, s: string, d?: string, dl?: string, n?: string, img?: string, size?: string, cm?: number, in?: number, c?: string, url?: string }} Variant
 * @typedef {{ id: number, family: string, name: string, short?: string, legacy?: boolean, options: ProductOption[], variants: Variant[] }} Product
 * @typedef {{ linked: boolean, variantId?: string }} Line - linked lines follow ball 1's pick
 */
class ProductDealBuilder extends HTMLElement {
  /** @type {Map<string, Product>} */
  products = new Map();

  /** @type {Map<string, { product: Product, variant: Variant }>} */
  variants = new Map();

  /** @type {Line[]} */
  lines = [];

  pump = false;

  /** @type {number | 'add' | null} row open in the sheet: a line index (≥ 1) or 'add' for a ball not yet in the set */
  sheetRow = null;

  /** @type {Line | null} the ball being composed in add mode */
  pending = null;

  connectedCallback() {
    if (this.initialized) {
      // Re-attached (moved by a re-render): disconnectedCallback dropped the section and document listeners (T44).
      this.listenOutside();
      this.render();
      return;
    }
    if (this.pendingInit) return;
    this.pendingInit = true;
    Promise.all([customElements.whenDefined('bigens-set-tray'), customElements.whenDefined('bigens-sticky-bar')]).then(
      () => {
        this.pendingInit = false;
        if (this.isConnected) this.init();
      },
    );
  }

  init() {
    try {
      this.data = JSON.parse(this.querySelector('[data-bundle-data]')?.textContent || '{}');
      this.strings = JSON.parse(this.querySelector('[data-bundle-strings]')?.textContent || '{}');
      this.querySelectorAll('[data-bundle-products]').forEach((script) => {
        JSON.parse(script.textContent || '[]').forEach((/** @type {Product} */ product) => this.addProduct(product));
      });
    } catch (error) {
      console.error('[bundle builder] Invalid data.', error);
      return;
    }

    (this.data.products || []).forEach((/** @type {Product} */ product) => this.addProduct(product, true));
    this.current = this.products.get(String(this.data.current));
    if (!this.current) return;

    this.tiers = Array.isArray(this.data.tiers) ? this.data.tiers.map(Number) : [0, 0, 0, 0];
    this.maxItems = Math.max(4, Number(this.data.max) || 12);
    this.lines = [{ linked: false, variantId: String(this.data.selected) }];
    this.sheet = this.querySelector('[data-bundle-sheet]');
    this.tray = /** @type {any} */ (this.querySelector('bigens-set-tray'));
    this.sticky = /** @type {any} */ (this.querySelector('bigens-sticky-bar'));
    this.store = /** @type {any} */ (customElements.get('bigens-set-tray'))?.store;

    this.addEventListener('change', this.handleChange);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('bigens-set-tray:add', () => this.openAddSheet());
    this.addEventListener('bigens-set-tray:change', (event) => this.showSheet(/** @type {any} */ (event).detail.index));
    this.addEventListener('bigens-set-tray:remove', (event) => this.removeRow(/** @type {any} */ (event).detail.index));
    this.sheet?.addEventListener('close', this.handleSheetClose);

    this.listenOutside();
    this.placeEtaLine();
    if (this.dataset.remember === 'true') this.restoreSet();

    this.initialized = true;
    this.render();
  }

  /** The picker's variant events (on the section) and cart updates (on the document). */
  listenOutside() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
    this.section = this.closest('.shopify-section');
    this.section?.addEventListener(StandardEvents.productSelect, this.handleProductSelect);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
    clearTimeout(this.buttonTimer);
  }

  /**
   * @param {Product} product
   * @param {boolean} [replace]
   */
  addProduct(product, replace = false) {
    const key = String(product.id);
    if (this.products.has(key) && !replace) return;
    this.products.set(key, product);
    product.variants.forEach((variant) => this.variants.set(String(variant.id), { product, variant }));
  }

  /* ---------- Model ---------- */

  /** @param {Line | null | undefined} line */
  resolve(line) {
    const id = !line || line.linked ? this.lines[0]?.variantId : line.variantId;
    return id ? this.variants.get(id) : undefined;
  }

  /** @param {Variant | undefined} variant */
  isOffered(variant) {
    return Boolean(variant && variant.a && variant.s !== 'waitlist');
  }

  /** @param {Product} product */
  defaultVariant(product) {
    return product.variants.find((variant) => this.isOffered(variant));
  }

  get hiddenState() {
    const ball = this.resolve(this.lines[0]);
    return !ball || ball.variant.s === 'waitlist';
  }

  /**
   * The families the sheet offers (T45, mockup D: family → size → colour across every product of a family). The
   * page's family first; other families only when mixing is on. T48 (D55): a legacy product (`legacy: true`, the old
   * 50″ from another manufacturer) is its own group under its own title, never merged into the Play Ball.
   * @returns {string[]}
   */
  families() {
    const current = this.current;
    if (!current) return [];
    const keys = [groupOf(current)];
    if (this.data.mix) {
      this.products.forEach((product) => {
        const key = groupOf(product);
        if ((product.family || product.legacy) && !keys.includes(key)) keys.push(key);
      });
    }
    return keys;
  }

  /**
   * Every ball of one family as { product, variant } (waitlist variants included, shown disabled).
   * @param {string} family
   */
  ballsIn(family) {
    /** @type {Array<{ product: Product, variant: Variant }>} */
    const balls = [];
    this.products.forEach((product) => {
      if (groupOf(product) !== family) return;
      product.variants.forEach((variant) => balls.push({ product, variant }));
    });
    return balls;
  }

  /** Every ball that can be bought now, over the families the sheet offers. */
  offeredBalls() {
    return this.families().flatMap((family) => this.ballsIn(family).filter(({ variant }) => this.isOffered(variant)));
  }

  /**
   * A ball's colour: its colour option value, else `bigens.color` (`c`); blank = no colour shown (never the family's).
   * @param {Product} product
   * @param {Variant} variant
   */
  colorOf(product, variant) {
    const index = product.options.findIndex((option) => option.kind === 'color');
    if (index >= 0) {
      const value = product.options[index].values.find((candidate) => candidate.v === variant.o[index]);
      return { name: variant.o[index] || '', hex: value?.hex };
    }
    const name = String(variant.c || '').trim();
    return { name, hex: name ? this.hexFor(name) : undefined };
  }

  /** @param {string} name */
  hexFor(name) {
    const wanted = name.toLowerCase();
    for (const product of this.products.values()) {
      const index = product.options.findIndex((option) => option.kind === 'color');
      if (index < 0) continue;
      const value = product.options[index].values.find((candidate) => candidate.v.trim().toLowerCase() === wanted && candidate.hex);
      if (value?.hex) return value.hex;
    }
    return window.CSS?.supports?.('color', wanted) ? wanted : undefined;
  }

  /**
   * A ball's size: key (cm, or the product for an unknown size) and the short label ("50 in" EN, "127 cm" nl/de/fr).
   * @param {Product} product
   * @param {Variant} variant
   */
  sizeOf(product, variant) {
    if (variant.cm && variant.in) {
      return { key: `cm-${variant.cm}`, cm: Number(variant.cm), label: this.text('sizeShort', { in: variant.in, cm: variant.cm }) };
    }
    const index = product.options.findIndex((option) => option.kind === 'size');
    const label =
      index >= 0
        ? product.options[index].values.find((value) => value.v === variant.o[index])?.label || variant.o[index]
        : variant.size || '';
    return { key: index >= 0 ? `o-${variant.o[index]}` : `p-${product.id}`, cm: Infinity, label: label || product.name };
  }

  /** "50 in Green" (or "50 in" without a colour name). @param {Product} product @param {Variant} variant */
  ballLabel(product, variant, withName = false) {
    // T48: next to its name, a legacy product needs no size: its title already names it ("The Original Bigens Ball, 50 in").
    const size = withName && product.legacy ? '' : this.sizeOf(product, variant).label;
    const parts = [size, this.colorOf(product, variant).name];
    if (withName) parts.unshift(product.short || product.name);
    return parts.filter(Boolean).join(' ');
  }

  /** @param {number} count */
  percentFor(count) {
    if (count < 2) return 0;
    return this.tiers[Math.min(count, 4) - 1] || 0;
  }

  /**
   * Totals for a list of balls (plus the pump when asked). Savings are rounded down per ball, then multiplied, like
   * Shopify's per-item allocation, so the shown total matches the cart to the cent (PM review fix 4; T17: per line
   * was 1 cent off at 3 × €89.71).
   * @param {Array<Variant | undefined>} balls
   * @param {boolean} [withPump]
   */
  totals(balls, withPump = false) {
    const percent = this.percentFor(balls.length);
    /** @type {Map<number, { price: number, quantity: number }>} */
    const grouped = new Map();
    balls.forEach((variant) => {
      if (!variant) return;
      const entry = grouped.get(variant.id) || { price: Number(variant.p) || 0, quantity: 0 };
      entry.quantity += 1;
      grouped.set(variant.id, entry);
    });

    let subtotal = 0;
    let saving = 0;
    grouped.forEach(({ price, quantity }) => {
      subtotal += price * quantity;
      saving += Math.floor((price * percent) / 100) * quantity;
    });

    const pump = withPump && this.pump && this.data.pump ? Number(this.data.pump.p) || 0 : 0;
    return { percent, saving, total: subtotal - saving + pump };
  }

  /** @param {number} count - the balls for a count: the current lines, topped up with ball 1's pick */
  ballsFor(count) {
    return Array.from({ length: count }, (_, index) => this.resolve(this.lines[index] || { linked: true })?.variant);
  }

  /** Restores the extra balls of a set saved by this PDP or the mix & match page (block setting, off by default). */
  restoreSet() {
    try {
      const saved = this.store?.read?.();
      if (!saved || !Array.isArray(saved.lines) || saved.lines.length < 2) return;
      const age = Date.now() - Date.parse(saved.updatedAt || '');
      if (!(age >= 0 && age < 7 * 24 * 60 * 60 * 1000)) return;

      // Anything that isn't a { variantId } object (hand-edited or corrupt storage) is skipped.
      const stored = saved.lines
        .filter((line) => line && typeof line === 'object' && line.variantId)
        .map((line) => String(line.variantId))
        .filter((id) => this.isOffered(this.variants.get(id)?.variant));
      const ownIndex = stored.indexOf(String(this.lines[0]?.variantId));
      if (ownIndex >= 0) stored.splice(ownIndex, 1);
      stored.slice(0, this.maxItems - 1).forEach((variantId) => this.lines.push({ linked: false, variantId }));
    } catch {
      this.lines = this.lines.slice(0, 1);
    }
  }

  /**
   * Called on shopper actions only (never on load), so opening a PDP leaves a set built elsewhere alone. A 1-ball
   * state is not a set: it clears a set this PDP saved earlier and never replaces one built on the mix & match page.
   */
  saveSet() {
    if (!this.store?.write) return;
    if (this.lines.length < 2) {
      if (this.store.read?.()?.source === 'pdp') this.store.clear?.();
      return;
    }
    const lines = [];
    for (const line of this.lines) {
      const selection = this.resolve(line);
      if (selection) lines.push({ productId: String(selection.product.id), variantId: String(selection.variant.id) });
    }
    this.store.write(lines, 'pdp');
  }

  /* ---------- Events ---------- */

  /** @param {Event} event */
  handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-bundle-pump-input]')) {
      this.pump = target.checked;
      this.render({ announce: true });
    } else if (target.matches('[data-sheet-family]')) {
      this.pickSheetFamily(target.value);
    } else if (target.matches('[data-sheet-size]')) {
      this.pickSheetSize(target.value);
    } else if (target.matches('[data-sheet-color]')) {
      const selection = this.variants.get(target.value);
      if (selection && this.isOffered(selection.variant)) this.setSheetVariant(selection.variant);
    }
  };

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target === this.sheet) {
      // A click on the backdrop (outside the sheet's content box) closes it.
      this.sheet.close();
      return;
    }
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;

    if (button.matches('[data-bundle-sheet-done]')) {
      this.commitSheet();
    } else if (button.matches('[data-bundle-sheet-close]')) {
      this.sheet?.close();
    } else if (button.matches('[data-bundle-combo]')) {
      this.applyCombo(button.dataset.bundleCombo || '');
    }
  };

  /** Ball 1 follows the stock variant picker. */
  handleProductSelect = (/** @type {any} */ event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card, quick-add-dialog')) return;
    const variantId = event.detail?.variantId;
    if (variantId) this.setBallOne(String(variantId));

    event.promise
      ?.then((/** @type {any} */ result) => {
        const id = result?.detail?.resource?.id;
        if (id) this.setBallOne(String(id));
        // Horizon morphs the add-to-cart buttons when this promise settles; re-apply the bundle label after it.
        this.scheduleButtonUpdate();
      })
      .catch(() => {});
  };

  handleCartUpdate = () => this.scheduleButtonUpdate();

  handleSheetClose = () => {
    const row = this.sheetRow;
    this.sheetRow = null;
    this.pending = null;
    this.render();
    if (row === 'add') this.tray?.focusAdd?.();
    else if (typeof row === 'number') this.tray?.focusSlot?.(row);
  };

  /** @param {string} variantId */
  setBallOne(variantId) {
    if (!this.variants.has(variantId) || this.lines[0]?.variantId === variantId) return;
    this.lines[0] = { linked: false, variantId };
    this.render();
    this.saveSet();
  }

  /** @param {number} index */
  removeRow(index) {
    if (index < 1 || index >= this.lines.length) return;
    this.lines.splice(index, 1);
    this.render({ announce: true });
    this.saveSet();
    this.tray?.focusSlot?.(index < this.lines.length ? index : this.lines.length - 1);
    if (index >= this.lines.length) this.tray?.focusAdd?.();
  }

  /** @param {string} spec - "productId:variantId,…" */
  applyCombo(spec) {
    const slots = spec
      .split(',')
      .map((slot) => slot.split(':'))
      .filter(([productId, variantId]) => productId && variantId);
    const ownIndex = slots.findIndex(([productId]) => productId === String(this.current?.id));
    if (ownIndex === -1) return;

    slots.splice(ownIndex, 1);
    this.lines = [this.lines[0], ...slots.map(([, variantId]) => ({ linked: false, variantId }))];
    this.render({ announce: true });
    this.saveSet();
  }

  /* ---------- Sheet (lazy: built on open) ---------- */

  /** The line the sheet edits: an existing ball, or the pending one in add mode. */
  get sheetLine() {
    if (this.sheetRow === 'add') return this.pending;
    return typeof this.sheetRow === 'number' ? this.lines[this.sheetRow] : null;
  }

  /** "+ Add another ball": the sheet opens on a new ball that starts as a copy of ball 1 and joins the set on "Add". */
  openAddSheet() {
    if (this.lines.length >= this.maxItems) return;
    const ballOne = this.resolve(this.lines[0]);
    if (!ballOne) return;
    // T45 (AUD2 #5): with one buyable ball there is nothing to choose; "Add another 50 in Green" adds it straight away.
    const offered = this.offeredBalls();
    if (offered.length === 1 && this.isOffered(ballOne.variant)) {
      const index = this.lines.length;
      this.lines.push({ linked: false, variantId: String(ballOne.variant.id) });
      this.render({ announce: true });
      this.saveSet();
      this.tray?.focusSlot?.(index);
      return;
    }
    if (!(this.sheet instanceof HTMLDialogElement)) return;
    this.pending = { linked: false, variantId: String(ballOne.variant.id) };
    this.sheetRow = 'add';
    this.renderSheet();
    if (!this.sheet.open) this.sheet.showModal();
    this.focusInSheet();
  }

  /**
   * Opens the sheet on one ball's editor (balls 2+; ball 1 follows the picker).
   * @param {number} row
   */
  showSheet(row) {
    if (!(this.sheet instanceof HTMLDialogElement) || !(row >= 1 && row < this.lines.length)) return;
    this.sheetRow = row;
    this.pending = null;
    this.renderSheet();
    if (!this.sheet.open) this.sheet.showModal();
    this.focusInSheet();
  }

  focusInSheet() {
    const target = this.sheet?.querySelector('input:checked:not(:disabled)') || this.sheet?.querySelector('[data-bundle-sheet-done]');
    if (target instanceof HTMLElement) target.focus();
  }

  /** "Add to set" / "Done": in add mode the pending ball joins the set, then the sheet closes. */
  commitSheet() {
    if (this.sheetRow === 'add' && this.pending) {
      const selection = this.resolve(this.pending);
      if (!selection || !this.isOffered(selection.variant)) {
        this.setError(this.text('chooseAvailable'));
        return;
      }
      const index = this.lines.length;
      this.lines.push(this.pending);
      this.pending = null;
      this.sheetRow = null;
      this.sheet?.close();
      this.render({ announce: true });
      this.saveSet();
      this.tray?.focusSlot?.(index);
      return;
    }
    this.sheet?.close();
  }

  /** @param {Variant} variant */
  setSheetVariant(variant) {
    const focusedName = document.activeElement instanceof HTMLInputElement ? document.activeElement.name : '';
    const line = { linked: false, variantId: String(variant.id) };
    if (this.sheetRow === 'add') this.pending = line;
    else if (typeof this.sheetRow === 'number') this.lines[this.sheetRow] = line;
    else return;
    this.renderSheet();
    if (this.sheetRow !== 'add') {
      this.render({ announce: true });
      this.saveSet();
    }
    if (focusedName) this.sheet?.querySelector(`input[name="${CSS.escape(focusedName)}"]:checked`)?.focus();
  }

  /**
   * Family tab: the first ball on offer in that family, the current size (cm) and colour name kept when possible.
   * @param {string} family
   */
  pickSheetFamily(family) {
    const current = this.resolve(this.sheetLine);
    const offered = this.ballsIn(family).filter(({ variant }) => this.isOffered(variant));
    if (offered.length === 0) return;
    const size = current ? this.sizeOf(current.product, current.variant).cm : Infinity;
    const color = current ? this.colorOf(current.product, current.variant).name.toLowerCase() : '';
    const score = (/** @type {{ product: Product, variant: Variant }} */ ball) =>
      (this.sizeOf(ball.product, ball.variant).cm === size ? 2 : 0) + (color && this.colorOf(ball.product, ball.variant).name.toLowerCase() === color ? 1 : 0);
    const best = offered.reduce((winner, ball) => (score(ball) > score(winner) ? ball : winner), offered[0]);
    this.setSheetVariant(best.variant);
  }

  /**
   * Size pill: a ball on offer in that size, the current colour name kept when possible.
   * @param {string} key
   */
  pickSheetSize(key) {
    const current = this.resolve(this.sheetLine);
    if (!current) return;
    const family = groupOf(current.product);
    const color = this.colorOf(current.product, current.variant).name.toLowerCase();
    const offered = this.ballsIn(family).filter(
      ({ product, variant }) => this.isOffered(variant) && this.sizeOf(product, variant).key === key,
    );
    const best =
      offered.find(({ product, variant }) => color && this.colorOf(product, variant).name.toLowerCase() === color) || offered[0];
    if (best) this.setSheetVariant(best.variant);
  }

  renderSheet() {
    const body = this.querySelector('[data-bundle-sheet-body]');
    const title = this.querySelector('[data-bundle-sheet-title]');
    const done = this.querySelector('[data-bundle-sheet-done]');
    const adding = this.sheetRow === 'add';
    if (title) {
      title.textContent = adding ? this.text('addTitle') : this.text('changeTitle', { n: Number(this.sheetRow) + 1 });
    }
    if (done) done.textContent = adding ? this.text('addToSet') : this.text('done');
    this.setError('');

    const selection = this.resolve(this.sheetLine);
    if (!body || !selection) return;
    const { product, variant } = selection;
    const uid = `${this.dataset.productId}-${this.dataset.sectionId}`;
    const family = groupOf(product);
    const nodes = [];

    // 1. Family tabs, stage-aware: "pre-order" when that family ships later, disabled with "waitlist" / "sold out".
    const families = this.families();
    if (families.length > 1) {
      nodes.push(
        el(
          'fieldset',
          { class: 'bigens-bundle__tabs' },
          el('legend', { class: 'visually-hidden' }, this.text('familyLabel')),
          ...families.map((key) => {
            const balls = this.ballsIn(key);
            const offered = balls.filter(({ variant: candidate }) => this.isOffered(candidate));
            const note =
              offered.length > 0
                ? offered.every(({ variant: candidate }) => candidate.s === 'preorder')
                  ? this.text('tabPreorder')
                  : ''
                : balls.some(({ variant: candidate }) => candidate.s === 'waitlist')
                  ? this.text('tabWaitlist')
                  : this.text('soldOut');
            const name = balls[0]?.product.name || key;
            return el(
              'label',
              { class: 'bigens-bundle__tab', 'data-family': key },
              el('input', {
                type: 'radio',
                class: 'bigens-bundle__input',
                name: `bigens-sheet-family-${uid}`,
                value: key,
                'data-sheet-family': '',
                checked: key === family,
                disabled: offered.length === 0,
              }),
              el('span', { class: 'bigens-set-ball bigens-bundle__tab-ball', 'aria-hidden': 'true' }),
              el('span', { class: 'bigens-bundle__tab-text' }, name, note ? el('span', { class: 'bigens-bundle__tab-note' }, ` · ${note}`) : ''),
            );
          }),
        ),
      );
    }

    // 2. Sizes of this family (every product merged, smallest first). Sold-out sizes stay visible, disabled.
    const balls = this.ballsIn(family).filter(({ variant: candidate }) => candidate.s !== 'waitlist' || this.isOffered(candidate));
    /** @type {Map<string, { label: string, cm: number, offered: boolean }>} */
    const sizes = new Map();
    balls.forEach(({ product: candidateProduct, variant: candidate }) => {
      const size = this.sizeOf(candidateProduct, candidate);
      const entry = sizes.get(size.key) || { label: size.label, cm: size.cm, offered: false };
      entry.offered = entry.offered || this.isOffered(candidate);
      sizes.set(size.key, entry);
    });
    const currentSize = this.sizeOf(product, variant);
    if (sizes.size > 1) {
      nodes.push(
        el(
          'fieldset',
          { class: 'bigens-bundle__sheet-option bigens-bundle__sheet-option--size' },
          el('legend', { class: 'bigens-bundle__sheet-legend' }, this.text('sizeLabel')),
          el(
            'div',
            { class: 'bigens-bundle__choices' },
            ...Array.from(sizes.entries())
              .sort((a, b) => a[1].cm - b[1].cm)
              .map(([key, size]) =>
                el(
                  'label',
                  { class: 'bigens-bundle__pill' },
                  el('input', {
                    type: 'radio',
                    class: 'bigens-bundle__input',
                    name: `bigens-sheet-size-${uid}`,
                    value: key,
                    'data-sheet-size': '',
                    checked: key === currentSize.key,
                    disabled: !size.offered,
                  }),
                  el('span', { class: 'bigens-bundle__pill-label' }, size.label),
                  size.offered ? '' : el('span', { class: 'bigens-bundle__pill-note' }, this.text('soldOut')),
                ),
              ),
          ),
        ),
      );
    }

    // 3. Colours of the picked size: one swatch per colour name (a ball without a name shows its photo).
    /** @type {Map<string, { product: Product, variant: Variant, name: string, hex?: string }>} */
    const colors = new Map();
    balls
      .filter(({ product: candidateProduct, variant: candidate }) => this.sizeOf(candidateProduct, candidate).key === currentSize.key)
      .forEach(({ product: candidateProduct, variant: candidate }) => {
        const color = this.colorOf(candidateProduct, candidate);
        const key = color.name ? color.name.toLowerCase() : `v-${candidate.id}`;
        const existing = colors.get(key);
        if (!existing || (!this.isOffered(existing.variant) && this.isOffered(candidate))) {
          colors.set(key, { product: candidateProduct, variant: candidate, name: color.name, hex: color.hex || existing?.hex });
        }
      });
    const currentColor = this.colorOf(product, variant);
    if (colors.size > 1 || currentColor.name) {
      nodes.push(
        el(
          'fieldset',
          { class: 'bigens-bundle__sheet-option bigens-bundle__sheet-option--color' },
          el(
            'legend',
            { class: 'bigens-bundle__sheet-legend' },
            this.text('colorLabel'),
            currentColor.name ? el('span', { class: 'bigens-bundle__sheet-value' }, currentColor.name) : '',
          ),
          el(
            'div',
            { class: 'bigens-bundle__choices' },
            ...Array.from(colors.values()).map((color) => {
              const offered = this.isOffered(color.variant);
              const label = color.name || color.product.name;
              return el(
                'label',
                { class: 'bigens-bundle__swatch', title: label },
                el('input', {
                  type: 'radio',
                  class: 'bigens-bundle__input',
                  name: `bigens-sheet-color-${uid}`,
                  value: String(color.variant.id),
                  'data-sheet-color': '',
                  checked: String(color.variant.id) === String(variant.id),
                  disabled: !offered,
                }),
                color.hex || !color.variant.img
                  ? ball(color.hex || familyColor(color.product.family))
                  : el('img', { class: 'bigens-set-ball bigens-bundle__swatch-image', src: color.variant.img, alt: '', width: '32', height: '32', 'aria-hidden': 'true' }),
                el('span', { class: 'visually-hidden' }, offered ? label : `${label}, ${this.text('soldOut')}`),
              );
            }),
          ),
        ),
      );
    }

    nodes.push(
      el(
        'p',
        { class: 'bigens-bundle__sheet-summary' },
        el('span', { class: 'bigens-bundle__sheet-ball' }, this.ballLabel(product, variant, true)),
        el('strong', {}, this.formatMoney(variant.p)),
        variant.s === 'preorder' && variant.n ? el('span', { class: 'bigens-bundle__ships' }, variant.n) : '',
      ),
    );

    body.replaceChildren(...nodes);
    body.style.setProperty('--family-accent', familyColor(product.legacy ? '' : product.family));
  }

  /* ---------- Rendering ---------- */

  /**
   * @param {{ announce?: boolean }} [options]
   */
  render({ announce = false } = {}) {
    const hidden = this.hiddenState;
    this.hidden = hidden;
    this.setError('');

    const count = this.lines.length;
    const balls = this.ballsFor(count);
    const { saving, total } = this.totals(balls, true);

    // The tray: slots, tier bar, nudge ("one more ball and every ball drops to …", hidden at 4+ and at the maximum).
    let nudge = null;
    if (count < 4 && count < this.maxItems) {
      const next = this.ballsFor(count + 1);
      const nextTotals = this.totals(next);
      const prices = new Set(next.map((variant) => Number(variant?.p) || 0));
      nudge =
        prices.size === 1
          ? { price: this.formatMoney(Math.ceil(nextTotals.total / next.length)) }
          : { percent: nextTotals.percent };
    }
    // T52 reference v2: the price per ball at 2, 3 and 4 balls for the empty slots, only while every ball in that set
    // has the same price (mixed sets fall back to "save n%"); rounded up like the nudge.
    /** @type {Record<number, string>} */
    const each = {};
    for (let size = 2; size <= Math.min(4, this.maxItems); size += 1) {
      const set = this.ballsFor(size);
      const prices = new Set(set.map((variant) => Number(variant?.p) || 0));
      if (set.every(Boolean) && prices.size === 1) each[size] = this.formatMoney(Math.ceil(this.totals(set).total / size));
    }
    // T45 (AUD2 #5): honest add button. One buyable ball → "Add another 50 in Green" + the mixed-set link.
    const ballOne = this.resolve(this.lines[0]);
    const single = Boolean(ballOne && this.isOffered(ballOne.variant) && this.offeredBalls().length === 1);
    this.tray?.update?.({
      balls: this.lines.map((line, index) => this.ballView(line, index)),
      tiers: this.tiers,
      max: this.maxItems,
      nudge,
      each,
      add: single && ballOne
        ? { label: this.text('addSame', { ball: this.ballLabel(ballOne.product, ballOne.variant) }), hint: '', link: true }
        : {
            label: this.text('addAnother'),
            // "any family, size or color" only while another family can really be added.
            hint: this.text(this.families().length > 1 ? 'addHint' : 'addHintSingle'),
            link: false,
          },
    });

    // Combos
    this.querySelectorAll('[data-bundle-combo]').forEach((button) => {
      const slots = (button.getAttribute('data-bundle-combo') || '').split(',');
      const own = String(this.current?.id);
      let ownUsed = false;
      const comboBalls = slots.map((slot) => {
        const [productId, variantId] = slot.split(':');
        if (productId === own && !ownUsed) {
          ownUsed = true;
          return balls[0];
        }
        return this.variants.get(variantId || '')?.variant;
      });
      const price = button.querySelector('[data-combo-price]');
      if (price) price.textContent = this.formatMoney(this.totals(comboBalls).total);
    });

    this.updateBuyButtons();
    this.updateEtaLine();

    if (announce && !hidden) {
      const status = this.querySelector('[data-bundle-status]');
      if (status) {
        const parts = [this.text(count === 1 ? 'statusOne' : 'status', { count, total: this.formatMoney(total) })];
        if (saving > 0) parts.push(this.text('youSave', { amount: this.formatMoney(saving) }));
        status.textContent = parts.join('. ');
      }
    }
  }

  /**
   * One ball as the tray shows it: short family name + size, colour, the swatch colour or image, flags.
   * @param {Line} line
   * @param {number} index
   */
  ballView(line, index) {
    const selection = this.resolve(line);
    if (!selection) return null;
    const { product, variant } = selection;
    const color = this.colorOf(product, variant);
    return {
      family: product.legacy ? '' : product.family,
      name: product.short || product.name,
      size: product.legacy ? '' : this.sizeOf(product, variant).label,
      color: color.name,
      hex: color.hex,
      img: variant.img,
      preorder: variant.s === 'preorder',
      offered: this.isOffered(variant),
      fixed: index === 0,
    };
  }

  /** "Play 50 in Green · Hoppi 39 in Pink · save 10 %" for the sticky bar. */
  summaryText() {
    // Identical balls are counted once ("2 × Play 50 in Green"), so the line stays short and readable (T45).
    /** @type {Map<string, number>} */
    const counted = new Map();
    this.lines
      .map((line, index) => this.ballView(line, index))
      .filter(Boolean)
      .forEach((ball) => {
        const label = [ball?.name, ball?.size, ball?.color].filter(Boolean).join(' ');
        counted.set(label, (counted.get(label) || 0) + 1);
      });
    const parts = Array.from(counted.entries()).map(([label, count]) => (count > 1 ? `${count} × ${label}` : label));
    const percent = this.percentFor(this.lines.length);
    if (percent > 0) parts.push(this.text('savePercent', { percent }));
    return parts.join(' · ');
  }

  /** The "You save …" line lives right under the buy buttons (the block itself sits above the picker). */
  placeEtaLine() {
    const eta = this.querySelector('[data-bundle-eta]');
    const buyBlock = this.section?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"]`,
    )?.closest('.buy-buttons-block');
    if (eta && buyBlock?.parentElement) buyBlock.insertAdjacentElement('afterend', eta);
    this.etaLine = eta;
  }

  /**
   * "You save {x} · {family} arrives about {date} · {other family} ships {month} (one parcel)". Only at 2+ balls.
   * Arrival = a range of business days from the theme settings (T45; live, available balls only); pre-order families
   * show the month of their latest expected ship date; D38 wording: an estimate, never a promise.
   */
  updateEtaLine() {
    const eta = this.etaLine;
    if (!(eta instanceof HTMLElement)) return;
    const count = this.lines.length;
    if (this.hiddenState || count < 2) {
      eta.hidden = true;
      eta.textContent = '';
      return;
    }
    const { saving } = this.totals(this.ballsFor(count), true);
    const parts = [];
    if (saving > 0) parts.push(this.text('youSave', { amount: this.formatMoney(saving) }));

    /** @type {Map<string, { name: string, live: boolean, preorder: string[] }>} */
    const families = new Map();
    this.lines.forEach((line) => {
      const selection = this.resolve(line);
      if (!selection) return;
      const key = groupOf(selection.product);
      const entry = families.get(key) || { name: selection.product.name, live: false, preorder: [] };
      if (selection.variant.s === 'preorder') entry.preorder.push(selection.variant.d || 'tbd');
      else entry.live = true;
      families.set(key, entry);
    });

    families.forEach(({ name, live, preorder }) => {
      if (preorder.length > 0) {
        const dated = preorder.filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date)).sort();
        const latest = dated[dated.length - 1];
        if (latest && dated.length === preorder.length) {
          parts.push(this.text('etaPreorder', { family: name, month: this.formatDate(new Date(`${latest}T12:00:00`), { month: 'short' }) }));
        } else {
          parts.push(this.text('etaPreorderTbd', { family: name }));
        }
      } else if (live) {
        // T45 (AUD3 #9): a range of business days from the theme settings, in the shopper's date format.
        parts.push(this.text('etaLive', { family: name, date: this.arrivalText() }));
      }
    });

    eta.textContent = `${parts.join(' · ')} ${this.text('oneParcel')}`.trim();
    eta.hidden = parts.length === 0;
  }

  /** "Oct 14 – 20": today + the theme's min/max business days (Monday to Friday), like snippets/bigens-arrival. */
  arrivalText() {
    const min = Number(this.dataset.arrivalMin) || 5;
    const max = Math.max(min, Number(this.dataset.arrivalMax) || min);
    const add = (/** @type {number} */ days) => {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      let added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) added += 1;
      }
      return date;
    };
    const start = add(min);
    const end = add(max);
    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    let format;
    try {
      format = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
    } catch {
      format = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
    }
    if (start.getTime() === end.getTime()) return format.format(start);
    return typeof format.formatRange === 'function' ? format.formatRange(start, end) : `${format.format(start)} – ${format.format(end)}`;
  }

  scheduleButtonUpdate() {
    clearTimeout(this.buttonTimer);
    this.buttonTimer = setTimeout(() => {
      this.updateBuyButtons();
      this.updateEtaLine();
    }, 0);
  }

  /**
   * Mirror the set on the buttons: the main button says "Add 2 balls to cart" with the set's total in the price chip,
   * Horizon's mobile sticky bar "Your set · 2 balls", and the desktop sticky bar gets its title, summary and total.
   */
  updateBuyButtons() {
    if (!this.section) return;
    const count = this.lines.length;
    const hidden = this.hiddenState;
    const bundled = !hidden && (count > 1 || (this.pump && this.data.pump));
    const { saving, total } = this.totals(this.ballsFor(count), true);
    const totalText = this.formatMoney(total);

    const mainButton = this.section.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"] [ref="addToCartButton"]`,
    );
    const stickyButton = this.section.querySelector(
      `sticky-add-to-cart[data-product-id="${this.dataset.productId}"] [ref="addToCartButton"]`,
    );

    [mainButton, stickyButton].forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const active = bundled && !button.disabled;

      const price = button.querySelector('.add-to-cart__price');
      if (price instanceof HTMLElement) {
        if (active) {
          if (price.dataset.bigensPrice === undefined) price.dataset.bigensPrice = price.textContent || '';
          price.textContent = totalText;
        } else if (price.dataset.bigensPrice !== undefined) {
          price.textContent = price.dataset.bigensPrice;
          delete price.dataset.bigensPrice;
        }
      }
      const compare = button.querySelector('.add-to-cart__compare-price');
      if (compare instanceof HTMLElement) compare.hidden = active;

      // The "You save" line under the button carries the saving; keep the in-button line empty.
      const savings = button.querySelector('.add-to-cart__savings');
      if (savings instanceof HTMLElement) {
        savings.textContent = '';
        savings.hidden = true;
      }

      const title = button.querySelector('.add-to-cart-text__title');
      const label = count > 1 && active ? this.text(button === stickyButton ? 'stickyTitle' : 'addBalls', { count }) : null;
      if (title) setLeadingText(title, label);
    });

    if (this.sticky?.update) {
      const main = mainButton instanceof HTMLButtonElement ? mainButton : null;
      this.sticky.target = main;
      const ballOne = this.resolve(this.lines[0]);
      const mainLabel = main?.querySelector('.add-to-cart-text__title');
      const stockLabel = mainLabel ? (mainLabel.dataset.bigensTitle ?? leadingText(mainLabel)) : '';
      // Phones (T45, AUD2 #4): "2 balls · €161,72" / "save 10% · add 1 more for 15%" + "Add set" (or the stock label).
      const percent = this.percentFor(count);
      const phoneSub = [];
      if (percent > 0) phoneSub.push(this.text('savePercentShort', { percent }));
      if (count < 4 && count < this.maxItems && this.percentFor(count + 1) > 0) {
        phoneSub.push(this.text('barMore', { percent: this.percentFor(count + 1) }));
      }
      this.sticky.update({
        enabled: !hidden && !!main && !main.disabled,
        title: count === 1 ? this.text('stickyTitleOne') : this.text('stickyTitle', { count }),
        summary: this.summaryText(),
        label: count > 1 ? this.text('stickyAdd', { count }) : stockLabel.trim(),
        total: totalText,
        thumb: ballOne?.variant.img || undefined,
        phoneTitle: `${count === 1 ? this.text('countOne') : this.text('countOther', { count })} · ${totalText}`,
        phoneSub: phoneSub.join(' · '),
        phoneLabel: count > 1 ? this.text('phoneAddSet') : stockLabel.trim(),
      });
    }

    void saving;
  }

  /* ---------- Cart (called by product-form.js) ---------- */

  /**
   * The cart lines for one /cart/add.js request, or undefined to let the stock single-line add run (1 ball, no pump,
   * or the builder is hidden for a waitlist pick), or null when a ball can't be bought.
   * @param {boolean} [showErrors]
   * @returns {Array<{ variantId: string, quantity: number, properties?: Record<string, string> }> | null | undefined}
   */
  getCartItems(showErrors = true) {
    if (!this.initialized || this.hiddenState) return undefined;
    if (this.lines.length === 1 && !(this.pump && this.data.pump)) return undefined;

    /** @type {Map<string, { variantId: string, quantity: number, properties?: Record<string, string> }>} */
    const grouped = new Map();
    for (const [index, line] of this.lines.entries()) {
      const selection = this.resolve(line);
      if (!selection || !this.isOffered(selection.variant)) {
        if (showErrors) {
          this.setError(this.text('chooseAvailable'));
          if (index > 0) this.tray?.focusSlot?.(index);
        }
        return null;
      }
      const { variant } = selection;
      const key = String(variant.id);
      const entry = grouped.get(key) || { variantId: key, quantity: 0, properties: this.lineProperties(variant) };
      entry.quantity += 1;
      grouped.set(key, entry);
    }

    const items = Array.from(grouped.values()).map((item) =>
      item.properties ? item : { variantId: item.variantId, quantity: item.quantity },
    );
    if (this.pump && this.data.pump) items.push({ variantId: String(this.data.pump.id), quantity: 1 });
    if (showErrors) this.setError('');
    return items;
  }

  /**
   * `_preorder` (YYYY-MM-DD, or `tbd`) + the visible ship date, the same as T11's bigens-preorder-properties.
   * @param {Variant} variant
   */
  lineProperties(variant) {
    if (variant.s !== 'preorder') return undefined;
    /** @type {Record<string, string>} */
    const properties = { _preorder: variant.d || 'tbd' };
    if (variant.dl && this.strings.propertyLabel) properties[this.strings.propertyLabel] = variant.dl;
    return properties;
  }

  /* ---------- Helpers ---------- */

  /** @param {string} message */
  setError(message) {
    const error = this.querySelector('[data-bundle-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = message === '';
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [values] - replaces `[name]` placeholders
   */
  text(key, values = {}) {
    let text = String(this.strings[key] || '');
    Object.entries(values).forEach(([name, value]) => {
      text = text.replaceAll(`[${name}]`, String(value));
    });
    return text;
  }

  /**
   * @param {Date} date
   * @param {Intl.DateTimeFormatOptions} options
   */
  formatDate(date, options) {
    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      return new Intl.DateTimeFormat(undefined, options).format(date);
    }
  }

  /** @param {number} cents */
  formatMoney(cents) {
    const moneyFormat = this.data.moneyFormat || '{{amount}}';
    const placeholder = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return String(cents);

    /** @type {Record<string, [number, string, string]>} */
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
    };
    const [precision, thousandsSeparator, decimalSeparator] = formats[placeholder[1]] || formats.amount;
    const [integerPart, decimalPart] = (Number(cents) / 100).toFixed(precision).split('.');
    const groupedInteger = (integerPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    const amount = decimalPart ? `${groupedInteger}${decimalSeparator}${decimalPart}` : groupedInteger;
    const decoder = document.createElement('template');
    decoder.innerHTML = moneyFormat.replace(placeholder[0], amount);
    return decoder.content.textContent?.trim() || amount;
  }
}

/**
 * The label's own text, without child elements (Horizon's sticky title also holds the "(qty)" span).
 * @param {Element} node
 */
function leadingText(node) {
  return Array.from(node.childNodes)
    .filter((child) => child.nodeType === Node.TEXT_NODE)
    .map((child) => child.textContent || '')
    .join('');
}

/**
 * Swaps the label's own text for the set label (or restores Horizon's), keeping child elements in place.
 * @param {Element} node
 * @param {string | null} label - null restores the original
 */
function setLeadingText(node, label) {
  if (!(node instanceof HTMLElement)) return;
  if (label !== null && node.dataset.bigensTitle === undefined) node.dataset.bigensTitle = leadingText(node);
  if (label === null && node.dataset.bigensTitle === undefined) return;
  const next = label === null ? node.dataset.bigensTitle || '' : label;
  const textNodes = Array.from(node.childNodes).filter((child) => child.nodeType === Node.TEXT_NODE);
  if (textNodes.length === 0) node.prepend(document.createTextNode(next));
  else {
    textNodes[0].textContent = next;
    textNodes.slice(1).forEach((extra) => extra.remove());
  }
  if (label === null) delete node.dataset.bigensTitle;
}

/**
 * Tiny DOM builder (text is always set as text, never as HTML).
 * @param {string} tag
 * @param {Record<string, string | boolean>} attributes
 * @param {...(Node | string)} children
 */
function el(tag, attributes = {}, ...children) {
  const node = document.createElement(tag);
  Object.entries(attributes).forEach(([name, value]) => {
    if (value === false) return;
    if (name === 'checked' || name === 'disabled') {
      /** @type {any} */ (node)[name] = value;
    }
    node.setAttribute(name, value === true ? '' : String(value));
  });
  children.forEach((child) => {
    if (child !== '' && child != null) node.append(child);
  });
  return node;
}

/** A colour ball with the Bigens shine (CSS in bigens-set-tray.css). @param {string} [color] */
function ball(color) {
  const node = el('span', { class: 'bigens-set-ball', 'aria-hidden': 'true' });
  if (color) node.style.setProperty('--ball-color', color);
  return node;
}

/**
 * The sheet group of a product: its family, or (T48, D55) its own id for a legacy product, so it is never merged into
 * the Play Ball's sizes and colours.
 * @param {Product} product
 */
function groupOf(product) {
  if (product.legacy) return `legacy-${product.id}`;
  return product.family || String(product.id);
}

/** @param {string} family */
function familyColor(family) {
  return family ? `var(--bigens-family-${family})` : 'var(--bigens-black)';
}

if (!customElements.get('product-deal-builder')) {
  customElements.define('product-deal-builder', ProductDealBuilder);
}
